import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cx } from '@/lib/cx';

export interface FormFieldProps {
  label: ReactNode;
  required?: boolean;
  /** エラーメッセージ（react-hook-form の errors.x?.message をそのまま渡す） */
  error?: string;
  /** 補足説明 */
  hint?: ReactNode;
  /** 'vertical'（ラベル上）| 'horizontal'（ラベル左。明細の多い帳票型フォーム向け） */
  layout?: 'vertical' | 'horizontal';
  /** グリッド内での列数（FormGrid の columns に対して） */
  span?: 1 | 2 | 3 | 4;
  /** 入力要素の id。省略時は自動採番して子要素へ注入 */
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

/**
 * ラベル・必須マーク・エラー・補足をまとめたフィールド枠。
 * 子要素が 1 つの React 要素なら id / aria-describedby / invalid を自動注入する。
 */
export function FormField({ label, required, error, hint, layout = 'vertical', span, htmlFor, className, children }: FormFieldProps) {
  const autoId = useId();
  const id = htmlFor ?? `f${autoId.replace(/:/g, '')}`;
  const descId = `${id}-desc`;
  const hasDesc = !!error || !!hint;

  let control = children;
  if (isValidElement(children) && !htmlFor) {
    const el = children as ReactElement<Record<string, unknown>>;
    const isNative = typeof el.type === 'string';
    control = cloneElement(el, {
      id: (el.props.id as string | undefined) ?? id,
      'aria-describedby': hasDesc ? descId : undefined,
      'aria-required': required || undefined,
      ...(isNative
        ? { 'aria-invalid': !!error || undefined }
        : { invalid: (el.props.invalid as boolean | undefined) ?? (!!error || undefined) }),
    });
  }

  return (
    <div
      className={cx('bs-field', `bs-field--${layout}`, error && 'has-error', className)}
      style={span ? { gridColumn: `span ${span}` } : undefined}
    >
      <label className="bs-field__label" htmlFor={id}>
        <span>{label}</span>
        {required && (
          <span className="bs-field__req" aria-label="必須">
            必須
          </span>
        )}
      </label>
      <div className="bs-field__body">
        {control}
        {hasDesc && (
          <div id={descId} className={cx('bs-field__desc', error && 'is-error')} role={error ? 'alert' : undefined}>
            {error ?? hint}
          </div>
        )}
      </div>
    </div>
  );
}

/** フィールドを等幅グリッドに並べる */
export function FormGrid({ columns = 2, children, className }: { columns?: 1 | 2 | 3 | 4; children: ReactNode; className?: string }) {
  return <div className={cx('bs-form-grid', className)} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>{children}</div>;
}

/** フォーム下部の操作ボタン領域（右寄せ・スクロール時に下部へ固定） */
export function FormActions({ children, sticky = true }: { children: ReactNode; sticky?: boolean }) {
  return <div className={cx('bs-form-actions', sticky && 'is-sticky')}>{children}</div>;
}
